/**
 * Helper for updating infinite query cache data.
 *
 * Handles insert, update, and delete operations on paginated data structures
 * used by TanStack Query's useInfiniteQuery.
 */

/**
 * Standard infinite query data structure from TanStack Query.
 * The items array key is configurable via the itemsKey parameter.
 */
export type InfiniteQueryData<T, K extends string = "items"> = {
    pages: (Record<K, T[]> & { nextToken?: string | null })[];
    pageParams: unknown[];
};

type PageWithItems<T, K extends string> = Record<K, T[]> & { nextToken?: string | null };

/**
 * Creates an updater function for infinite query cache data.
 *
 * @param itemId - The ID of the item being updated/inserted/deleted
 * @param item - The item to insert/update, or undefined to delete
 * @param itemsKey - The key in each page that contains the items array (e.g., "projects", "executions")
 * @param getId - Function to extract ID from an item
 * @returns An updater function suitable for use with queryClient.setQueryData
 */
export function createInfiniteQueryUpdater<T, K extends string = "items">(
    itemId: string,
    item: T | undefined,
    itemsKey: K,
    getId: (item: T) => string,
): (currentData: unknown) => InfiniteQueryData<T, K> {
    return (currentData: unknown): InfiniteQueryData<T, K> => {
        const infiniteData = currentData as InfiniteQueryData<T, K> | undefined;

        const isNewItem =
            !!item &&
            !infiniteData?.pages?.some((page) => (page[itemsKey] as T[]).some((p) => getId(p) === itemId));

        // Insert the new item at the beginning of the first page
        if (isNewItem) {
            const existingFirstPage = infiniteData?.pages[0];
            const existingItems = existingFirstPage ? (existingFirstPage[itemsKey] as T[]) : [];

            const newFirstPage = {
                nextToken: null,
                ...existingFirstPage,
                [itemsKey]: [item, ...existingItems],
            } as PageWithItems<T, K>;

            const pages = [newFirstPage, ...(infiniteData?.pages?.slice(1) ?? [])];
            return {
                pageParams: [""],
                ...infiniteData,
                pages,
            } as InfiniteQueryData<T, K>;
        }

        // If no current data, return empty structure
        // This handles the case when an update event is received while the list isn't loaded yet
        if (!infiniteData) {
            return {
                pages: [{ [itemsKey]: [], nextToken: null } as PageWithItems<T, K>],
                pageParams: [""],
            } as InfiniteQueryData<T, K>;
        }

        // Update or delete the existing item
        const updatedPages = infiniteData.pages.map((page) => {
            const items = page[itemsKey] as T[];
            if (!items) return page;

            // Update or delete the existing item
            const updatedItems = items
                .map((p) => (getId(p) === itemId ? item : p))
                .filter((w): w is T => w !== undefined);

            return {
                ...page,
                [itemsKey]: updatedItems,
            } as PageWithItems<T, K>;
        });

        return {
            ...infiniteData,
            pages: updatedPages,
        } as InfiniteQueryData<T, K>;
    };
}
